import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, Box, Users, FolderOpen, Gift, BookOpen, Shield, LogOut, Menu, Leaf, ChevronRight, Bell, Check, Database, Upload, Mail, FileText, Globe, ShoppingCart, Truck, MessageCircle, Volume2, BellRing, BellOff, UserCircle, Megaphone, ClipboardCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import { usePushNotifications } from '../../hooks/usePushNotifications';

const NAV_ITEMS = [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/admin/produits', label: 'Produits', icon: Box },
    { to: '/admin/clients', label: 'Clients', icon: Users },
    { to: '/admin/offres', label: 'Offres', icon: Gift },
    { to: '/admin/commandes', label: 'Commandes', icon: ClipboardList },
    { to: '/admin/devis', label: 'Demandes de prix', icon: FileText },
    { to: '/admin/appel-commandes', label: 'Appel Commandes', icon: Megaphone },
    { to: '/admin/bons-livraison', label: 'Bons de Livraison', icon: FileText },
    { to: '/admin/fournisseurs', label: 'Fournisseurs', icon: Truck },
    { to: '/admin/mises-a-jour-fournisseur', label: 'Maj Fournisseur', icon: ClipboardCheck },
    { to: '/admin/messages', label: 'Messagerie Fournisseur', icon: MessageCircle },
    { to: '/admin/bonnes-pratiques', label: 'Bonnes Pratiques', icon: BookOpen },
    { to: '/admin/catalogues', label: 'Catalogues', icon: FolderOpen },
    { to: '/admin/contacts', label: 'Messages Contact', icon: MessageCircle },
    { to: '/admin/visiteurs', label: 'Visiteurs', icon: Globe },
    { to: '/admin/emails', label: 'Suivi Emails', icon: Mail },
    { to: '/admin/administrateurs', label: 'Administrateurs', icon: Shield },
    { to: '/admin/profil', label: 'Mon Profil', icon: UserCircle },
];

const NavItem = ({ item, isActive, onClick, badge }) => (
    <Link
        to={item.to}
        onClick={onClick}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
            isActive
                ? 'bg-leaf text-white shadow-sm'
                : 'text-sand/70 hover:text-sand hover:bg-white/10'
        }`}
        data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
        <item.icon className="w-4 h-4 flex-shrink-0" />
        <span>{item.label}</span>
        {badge > 0 && (
            <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                {badge > 9 ? '9+' : badge}
            </span>
        )}
        {isActive && !badge && <ChevronRight className="w-3 h-3 ml-auto" />}
    </Link>
);

/* Notification Panel (shared between desktop and mobile) */
const NotifPanel = ({ notifications, onNavigate, onMarkRead, onMarkAllRead, onClose, pushProps }) => (
    <div className="w-80 max-w-[90vw] bg-white rounded-2xl shadow-2xl border border-jungle/10 overflow-hidden" data-testid="admin-notif-panel">
        <div className="bg-jungle p-3 flex items-center justify-between">
            <span className="text-sand font-semibold text-sm flex items-center gap-2">
                <Bell className="w-4 h-4" /> Notifications
            </span>
            {notifications.some(n => !n.is_read) && (
                <Button onClick={onMarkAllRead} variant="ghost" size="sm"
                    className="text-sand/70 hover:text-sand text-xs h-7 hover:bg-white/10"
                    data-testid="mark-all-read-btn">
                    <Check className="w-3 h-3 mr-1" /> Tout lire
                </Button>
            )}
        </div>
        <div className="max-h-72 overflow-y-auto">
            {notifications.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                    Aucune nouvelle notification
                </div>
            ) : (
                notifications.map(notif => {
                    const isQuote = notif.type === 'quote_request';
                    return (
                        <div key={notif.id}
                            onClick={() => onNavigate(notif)}
                            className={`p-3 border-b border-gray-100 cursor-pointer transition-colors ${
                                notif.is_read ? 'bg-gray-50/80' : isQuote ? 'hover:bg-amber-50 bg-white' : 'hover:bg-leaf/5 bg-white'
                            }`}
                            data-testid={`notif-${notif.id}`}>
                            <div className="flex items-start gap-3">
                                <div className={`flex-shrink-0 mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center ${
                                    notif.is_read
                                        ? 'bg-gray-100'
                                        : isQuote
                                            ? 'bg-amber-100'
                                            : 'bg-emerald-100'
                                }`}>
                                    {isQuote
                                        ? <FileText className={`w-4 h-4 ${notif.is_read ? 'text-gray-400' : 'text-amber-600'}`} />
                                        : <ShoppingCart className={`w-4 h-4 ${notif.is_read ? 'text-gray-400' : 'text-emerald-600'}`} />
                                    }
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium ${notif.is_read ? 'text-gray-400' : 'text-jungle'}`}>{notif.title}</p>
                                    <p className={`text-xs mt-0.5 ${notif.is_read ? 'text-gray-400' : 'text-muted-foreground'}`}>{notif.message}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <p className="text-[10px] text-muted-foreground/60">
                                            {new Date(notif.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                        {!notif.is_read && (
                                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                                                isQuote ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                                            }`}>
                                                {isQuote ? 'Demande de prix' : 'Commande'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {!notif.is_read && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onMarkRead(notif.id); }}
                                        className="flex-shrink-0 mt-1 px-2 py-1 text-[10px] font-medium rounded-full bg-leaf/10 text-leaf hover:bg-leaf/20 transition-colors"
                                        title="Marquer comme lu"
                                        data-testid={`notif-mark-read-${notif.id}`}
                                    >
                                        Lu
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })
            )}
        </div>
        <div className="p-2 bg-gray-50 border-t flex items-center gap-2">
            {pushProps?.supported && (
                <Button
                    onClick={pushProps.isSubscribed ? pushProps.unsubscribe : pushProps.subscribe}
                    variant="ghost" size="sm"
                    className={`text-xs flex-1 ${pushProps.isSubscribed ? 'text-green-600' : 'text-amber-600'}`}
                    disabled={pushProps.loading}
                    data-testid="push-toggle-btn"
                >
                    {pushProps.isSubscribed
                        ? <><BellRing className="w-3 h-3 mr-1" /> Push actif</>
                        : <><BellOff className="w-3 h-3 mr-1" /> Activer push</>
                    }
                </Button>
            )}
            <Button onClick={onClose} variant="ghost" size="sm" className="text-jungle text-xs flex-1">
                Fermer
            </Button>
        </div>
    </div>
);

export const AdminLayout = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notifOpen, setNotifOpen] = useState(false);
    const [notifCount, setNotifCount] = useState(0);
    const [ordersNotifCount, setOrdersNotifCount] = useState(0);
    const [quotesNotifCount, setQuotesNotifCount] = useState(0);
    const notifRef = useRef(null);
    const mobileNotifRef = useRef(null);
    const prevCountRef = useRef(0);
    const pushNotif = usePushNotifications();

    // Close on outside click
    useEffect(() => {
        if (!notifOpen) return;
        const handler = (e) => {
            const isOutsideDesktop = notifRef.current && !notifRef.current.contains(e.target);
            const isOutsideMobile = mobileNotifRef.current && !mobileNotifRef.current.contains(e.target);
            // Close only if click is outside both panels
            if (isOutsideDesktop && (!mobileNotifRef.current || isOutsideMobile)) {
                setNotifOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [notifOpen]);

    const fetchNotifCount = useCallback(async () => {
        try {
            const res = await adminAPI.getNotificationCount();
            setNotifCount(res.data.count);
            setOrdersNotifCount(res.data.pending_orders || 0);
            setQuotesNotifCount(res.data.pending_quotes || 0);
            prevCountRef.current = res.data.count;
        } catch (err) { console.error('Failed to fetch notification count:', err); }
    }, []);

    const fetchNotifications = async () => {
        try {
            const res = await adminAPI.getNotifications();
            setNotifications(res.data.notifications || []);
        } catch (err) { console.error('Failed to fetch notifications:', err); }
    };

    // Notification sound
    const playNotifSound = useCallback(() => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.setValueAtTime(880, ctx.currentTime);
            osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
            gain.gain.setValueAtTime(0.3, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.4);
        } catch (e) { console.error('Audio notification error:', e); }
    }, []);

    // SSE real-time connection with auto-reconnect
    useEffect(() => {
        fetchNotifCount();

        const token = localStorage.getItem('token');
        if (!token) return;

        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        let retryTimeout = null;
        let closed = false;

        const connect = () => {
            if (closed) return;
            const evtSource = new EventSource(`${baseUrl}/api/sse/notifications?token=${token}`);

            evtSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'init') {
                        setNotifCount(data.count);
                        setOrdersNotifCount(data.pending_orders || 0);
                        setQuotesNotifCount(data.pending_quotes || 0);
                        prevCountRef.current = data.count;
                    } else if (data.type === 'new_notification') {
                        setNotifCount(prev => prev + 1);
                        if (data.title?.startsWith('Nouvelle commande')) {
                            setOrdersNotifCount(prev => prev + 1);
                        } else if (data.title?.startsWith('Demande de prix')) {
                            setQuotesNotifCount(prev => prev + 1);
                        }
                        playNotifSound();
                        toast.info(data.title, {
                            description: data.message,
                            duration: 8000,
                        });
                        if (notifOpen) fetchNotifications();
                    }
                } catch (err) { console.error('SSE parse error:', err); }
            };

            evtSource.onerror = () => {
                evtSource.close();
                if (!closed) {
                    retryTimeout = setTimeout(connect, 5000);
                }
            };
        };

        connect();

        return () => {
            closed = true;
            if (retryTimeout) clearTimeout(retryTimeout);
        };
    }, [fetchNotifCount, playNotifSound]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleNotifClick = async () => {
        if (!notifOpen) await fetchNotifications();
        setNotifOpen(!notifOpen);
    };

    const handleMarkRead = async (notifId) => {
        await adminAPI.markNotificationRead(notifId);
        setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
        setNotifCount(prev => Math.max(0, prev - 1));
        prevCountRef.current = Math.max(0, prevCountRef.current - 1);
        // Menu badges (orders/quotes) are NOT decremented here.
        // They only change when the entity status changes (via fetchNotifCount polling).
    };

    const handleMarkAllRead = async () => {
        await adminAPI.markAllNotificationsRead();
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setNotifCount(0);
        prevCountRef.current = 0;
        // Menu badges stay unchanged - they reflect pending entity statuses
    };

    const handleNotifNavigate = async (notif) => {
        if (!notif.is_read) {
            await handleMarkRead(notif.id);
        }
        setNotifOpen(false);
        if (notif.type === 'quote_request' && notif.quote_id) {
            navigate('/admin/devis');
        } else if (notif.type === 'supplier_update') {
            navigate('/admin/mises-a-jour-fournisseur');
        } else if (notif.order_id) {
            navigate(`/admin/commande/${notif.order_id}`);
        } else {
            navigate('/admin/commandes');
        }
    };

    const handleCloseNotifPanel = () => {
        setNotifOpen(false);
    };

    const isActive = (path) => {
        if (path === '/admin') return location.pathname === '/admin';
        return location.pathname.startsWith(path);
    };

    const handleLogout = () => { logout(); navigate('/'); };

    const handleBackup = async () => {
        toast.info('Preparation de la sauvegarde...');
        try {
            const res = await adminAPI.backupDatabase();
            const blob = new Blob([res.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `prixdeal_backup_${new Date().toISOString().slice(0, 10)}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Sauvegarde telecharegee !');
        } catch (e) {
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const restoreFileRef = useRef(null);

    const handleRestore = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        if (!file.name.endsWith('.zip')) {
            toast.error('Veuillez selectionner un fichier .zip');
            return;
        }
        const confirmed = window.confirm(
            'ATTENTION : La restauration va REMPLACER toutes les donnees actuelles par celles du fichier de sauvegarde.\n\nEtes-vous sur de vouloir continuer ?'
        );
        if (!confirmed) return;
        toast.info('Restauration en cours...');
        try {
            const res = await adminAPI.restoreDatabase(file);
            const { restored, errors } = res.data;
            toast.success(`Restauration terminee ! ${restored.length} collection(s) restauree(s).`);
            if (errors.length > 0) {
                toast.warning(`${errors.length} avertissement(s) : ${errors.join(', ')}`);
            }
            setTimeout(() => window.location.reload(), 1500);
        } catch (err) {
            console.error('Restore error:', err);
            toast.error('Erreur lors de la restauration');
        }
    };

    const BellButton = ({ className = '', testId = 'admin-notif-bell' }) => (
        <Button onClick={handleNotifClick} variant="ghost" size="icon"
            className={`relative ${className}`} data-testid={testId}>
            <Bell className="w-5 h-5" />
            {notifCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                    {notifCount > 9 ? '9+' : notifCount}
                </span>
            )}
        </Button>
    );

    const SidebarContent = ({ onItemClick }) => (
        <div className="flex flex-col h-full">
            <div className="p-5 border-b border-white/10">
                <Link to="/" className="flex items-center gap-2.5 group">
                    <div className="w-9 h-9 rounded-lg bg-leaf flex items-center justify-center group-hover:scale-105 transition-transform">
                        <Leaf className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <span className="font-heading font-bold text-lg text-sand">PRIXDEAL</span>
                        <span className="block text-[10px] text-sand/50 uppercase tracking-wider leading-none">Administration</span>
                    </div>
                </Link>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {NAV_ITEMS.map((item) => {
                    let badge = 0;
                    if (item.to === '/admin/commandes') badge = ordersNotifCount;
                    else if (item.to === '/admin/devis') badge = quotesNotifCount;
                    return (
                        <NavItem key={item.to} item={item} isActive={isActive(item.to)}
                            onClick={onItemClick}
                            badge={badge} />
                    );
                })}
            </nav>
            <div className="p-4 border-t border-white/10">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-leaf/20 flex items-center justify-center text-xs font-bold text-leaf">
                        {user?.contact_name?.charAt(0)?.toUpperCase() || 'A'}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-sand truncate">{user?.contact_name || 'Admin'}</p>
                        <p className="text-xs text-sand/50 truncate">{user?.email}</p>
                    </div>
                </div>
                <Button onClick={handleBackup} variant="ghost" size="sm"
                    className="w-full justify-start text-sand/60 hover:text-leaf hover:bg-leaf/10 mb-1"
                    data-testid="admin-backup-btn">
                    <Database className="w-4 h-4 mr-2" /> Sauvegarder BDD
                </Button>
                <Button onClick={() => restoreFileRef.current?.click()} variant="ghost" size="sm"
                    className="w-full justify-start text-sand/60 hover:text-amber-300 hover:bg-amber-500/10 mb-1"
                    data-testid="admin-restore-btn">
                    <Upload className="w-4 h-4 mr-2" /> Restaurer BDD
                </Button>
                <input ref={restoreFileRef} type="file" accept=".zip" className="hidden" onChange={handleRestore} />
                <Button onClick={handleLogout} variant="ghost" size="sm"
                    className="w-full justify-start text-sand/60 hover:text-red-300 hover:bg-red-500/10"
                    data-testid="admin-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Deconnexion
                </Button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-sand" data-testid="admin-layout">
            {/* Desktop Sidebar */}
            <aside className="fixed left-0 top-0 bottom-0 w-60 bg-jungle z-40 hidden lg:flex flex-col">
                <SidebarContent />
            </aside>

            {/* Mobile Top Bar */}
            <div className="lg:hidden sticky top-0 z-50 bg-jungle/95 backdrop-blur-md border-b border-white/10">
                <div className="flex items-center justify-between h-14 px-4">
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-sand hover:bg-white/10">
                                <Menu className="w-5 h-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-60 p-0 bg-jungle border-r-0">
                            <SidebarContent onItemClick={() => setMobileOpen(false)} />
                        </SheetContent>
                    </Sheet>
                    <Link to="/" className="flex items-center gap-2">
                        <Leaf className="w-5 h-5 text-leaf" />
                        <span className="font-heading font-bold text-sand">PRIXDEAL</span>
                    </Link>
                    <div className="flex items-center gap-1">
                        <BellButton testId="admin-notif-bell-mobile" className="text-sand/60 hover:text-sand hover:bg-white/10" />
                        <Button onClick={handleLogout} variant="ghost" size="icon"
                            className="text-sand/60 hover:text-red-300 hover:bg-red-500/10">
                            <LogOut className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Desktop notification bell (fixed top-right) */}
            <div ref={notifRef} className="hidden lg:block fixed top-4 right-4 z-[65]">
                <div className="relative">
                    <Button onClick={handleNotifClick} variant="outline" size="icon"
                        className="bg-white/90 backdrop-blur-sm border-jungle/10 hover:bg-white relative shadow-sm"
                        data-testid="admin-notif-bell">
                        <Bell className="w-4 h-4 text-jungle" />
                        {notifCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                                {notifCount > 9 ? '9+' : notifCount}
                            </span>
                        )}
                    </Button>
                    {notifOpen && (
                        <div className="absolute top-12 right-0">
                            <NotifPanel notifications={notifications}
                                onNavigate={handleNotifNavigate}
                                onMarkRead={handleMarkRead}
                                onMarkAllRead={handleMarkAllRead}
                                onClose={handleCloseNotifPanel}
                                pushProps={pushNotif} />
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile notification panel (overlay) */}
            {notifOpen && (
                <div className="lg:hidden fixed inset-0 z-[60] flex items-start justify-center pt-16">
                    <div className="fixed inset-0 bg-black/30" onClick={() => setNotifOpen(false)} />
                    <div className="relative z-10" ref={mobileNotifRef}>
                        <NotifPanel notifications={notifications}
                            onNavigate={handleNotifNavigate}
                            onMarkRead={handleMarkRead}
                            onMarkAllRead={handleMarkAllRead}
                            onClose={handleCloseNotifPanel}
                            pushProps={pushNotif} />
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main className="lg:ml-60 min-h-screen">
                {children}
            </main>
        </div>
    );
};
